<script setup lang="ts">
import { useAuthStore } from '~/stores/auth'

const authStore = useAuthStore()

const { t } = useI18n()
const { updateUser } = useUserService()

const emit = defineEmits<{
  (e: 'confirmed'): void
  (e: 'close'): void
}>()

const state = reactive<{
  checked: boolean
  saving: boolean
}>({
  checked: false,
  saving: false
})

const onCancel = () => {
  emit('close')
}

const onConfirm = async () => {
  if (!state.checked || state.saving || !authStore.currentUser) {
    return
  }

  state.saving = true

  try {
    const updatedUser = await authStore.awaitAndSetLatestUserData(
      updateUser(authStore.currentUser.id, { ai_image_consent: true })
    )

    if (!updatedUser) {
      return
    }

    emit('confirmed')
  } catch (error) {
    console.error('Error saving AI image consent', error)
  } finally {
    state.saving = false
  }
}
</script>

<template>
  <Modal force-answer max-height="520px" @close="onCancel">
    <template #header>
      <span class="tw-font-medium">{{ t('ai_creation_consent_box_title') }}</span>
    </template>

    <template #body>
      <div class="tw-flex tw-flex-col tw-gap-4 tw-py-2">
        <p class="tw-text-sm tw-leading-snug tw-text-gray-700">
          {{ t('ai_creation_consent_box_body') }}
        </p>

        <label class="tw-flex tw-cursor-pointer tw-items-start tw-gap-3">
          <input v-model="state.checked" type="checkbox" class="tw-mt-1" :disabled="state.saving" />

          <i18n-t
            keypath="ai_creation_consent_box_checkbox"
            tag="span"
            class="tw-text-sm tw-leading-snug tw-text-gray-700"
          >
            <a
              href="https://cardoride.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              class="tw-underline"
              >{{ t('Privacy Policy') }}</a
            >
            <a
              href="https://openai.com/policies/terms-of-use/"
              target="_blank"
              rel="noopener noreferrer"
              class="tw-underline"
              >{{ t('Terms of Service') }}</a
            >
            <a
              href="https://openai.com/policies/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              class="tw-underline"
              >{{ t('Privacy Policy') }}</a
            >
          </i18n-t>
        </label>
      </div>
    </template>

    <template #footer>
      <div class="tw-flex tw-w-full tw-flex-col tw-gap-3 tw-px-4 tw-py-4">
        <InputAppButton
          class="tw-w-full tw-rounded-full tw-bg-riser-blue tw-px-4 tw-py-3 tw-font-bold tw-text-white"
          :disabled="!state.checked"
          :loading="state.saving"
          @click="onConfirm"
        >
          {{ t('Ok') }}
        </InputAppButton>

        <InputAppButton
          class="tw-w-full tw-py-2 tw-text-center tw-text-sm tw-text-riser-blue"
          :disabled="state.saving"
          @click="onCancel"
        >
          {{ t('Cancel') }}
        </InputAppButton>
      </div>
    </template>
  </Modal>
</template>
